import { FilterOperations } from 'business/definitions';
import { snakify } from 'helpers/syntaxHelper';
import { sortBy } from 'lodash';
import { ChartDataQueryPayload } from '../ChartContainer';
import { BreadCrumbNodeT } from '../types';

const transformPlainToDict = (
    value: PlainResponseDataValueT,
    prefix: string,
): DictDataValueT => ({
    label: `${prefix} ${value.toString()}`,
    value: value.toString(),
});

/** *
 * Prefix the data response field value with a given prefix.
 */
const prefixDataResponse = (
    data: ResponseDataT,
    field: string,
    prefix: string,
) => {
    const el = data;
    const value: ResponseDataValueT = data[field];
    if (value === null) return;

    // DictAllDataValueT case
    if (typeof value === 'object' && 'label' in value) {
        value.label = `${prefix} ${value.label}`;
        return;
    }

    // PlainResponseDataValueT case
    if (typeof value === 'string') {
        // we tranform the value to a DictDataValueT in order to have a prefixed label and a value untouched
        el[field] = transformPlainToDict(value, prefix);
    }
};

/**
 * Transform the data response field value into a DictAllDataValueT if it is a string.
 */
const transformDataResponse = (data: ResponseDataT, field: string) => {
    const el = data;
    const value: ResponseDataValueT = data[field];
    if (value === null) return;

    if (typeof value === 'string') {
        el[field] = {
            label: value.toString(),
            value: value.toString(),
        };
    }
};

/**
 * Prefix the parent data label with a given prefix.
 * The field to prefix in the data is the last breadcrumb field.
 */
export const prefixParentDataLabel = (
    breadcrumbs: BreadCrumbNodeT[],
    parentData: ResponseDataT[],
    data: ResponseDataT[],
    prefix: string,
) => {
    if (parentData.length === 0) return;
    if (!breadcrumbs || breadcrumbs.length === 0) return;

    const field: string = breadcrumbs[breadcrumbs.length - 1].field!;
    const parentValue: ResponseDataValueT = parentData[0][field];
    if (parentValue === null) return;

    parentData.forEach((element) => {
        prefixDataResponse(element, field, prefix);
    });

    const shouldTransformData = typeof parentValue === 'string';
    if (shouldTransformData)
        data.forEach((element) => transformDataResponse(element, field));
};

/**
 * Create the parent chart data maker payload.
 * This payload is to be used with useChartDataMaker hook.
 */
export const createParentChartDataMakerPayload = (
    childrenPayload: ChartDataQueryPayload,
    isUpdateChartLoading: boolean,
    breadcrumbs: BreadCrumbNodeT[],
): ChartDataQueryPayload => {
    const {
        chartKey,
        publicToken,
        queryFilters,
        queryMetrics,
        queryDimensions,
    } = childrenPayload;

    const shouldHandleParentData = breadcrumbs && breadcrumbs.length > 1;

    const parentChartDataMakerPayload: ChartDataQueryPayload = {
        chartKey,
        queryMetrics,
        queryDimensions,
        queryFilters,
        extraFilters: [],
        cacheKey: [],
        publicToken,
        enabled: !isUpdateChartLoading && shouldHandleParentData,
    };

    if (!shouldHandleParentData) return parentChartDataMakerPayload;

    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

    const filters = [
        {
            field: snakify(lastBreadcrumb.field!),
            value: lastBreadcrumb.value,
            operation: FilterOperations.in_,
            isNavigation: true,
        } as QueryFilterT,
    ];

    const parentCacheKey = [
        sortBy(queryMetrics, (m) => m.field),
        sortBy(queryFilters, (f) => f.field),
        sortBy(queryDimensions, (d) => d.field),
        sortBy(filters, (ef) => ef.field),
    ];

    parentChartDataMakerPayload.extraFilters = filters;
    parentChartDataMakerPayload.cacheKey = parentCacheKey;

    return parentChartDataMakerPayload;
};
